import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useEntitlements } from "../hooks/useEntitlements";
import "../styles/entitlements.css"; // el css reusable que hicimos



import "../styles/generalStyle.css";
import "./ChatSupport.css";

import { useLanguage } from "../i18n/LanguageContext";


export async function clearChatNotifs(uid) {
  const ref = doc(db, "usuarios", uid, "notifs", "summary");
  await setDoc(
    ref,
    {
      chatUnread: 0,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}


export default function ChatSupport() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [uid, setUid] = useState(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  const endRef = useRef(null);

  const { loading: entLoading, canSong, entitlements, consumeSongCredit } = useEntitlements(uid);
    const [showPaywall, setShowPaywall] = useState(false);

    async function onCrearCancion() {
    // si todavía no cargan los entitlements, no hagas nada
    if (entLoading) return;

    if (!canSong) {
        setShowPaywall(true);
        return;
    }

    // 1) consume crédito (y te dice si fue demo o paid)
    const { used } = await consumeSongCredit();

    // 2) ahora sí crea la canción y guarda used
    // used === "demo" | "paid"
    }

  // ---- horario (local del navegador)
  const support = useMemo(() => {
    // Lunes=1 ... Domingo=0 en JS: getDay(): 0 dom, 1 lun, ... 6 sáb
    const now = new Date();
    const day = now.getDay();
    const hour = now.getHours();

    const isWeekday = day >= 1 && day <= 5;
    const inHours = hour >= 9 && hour < 17; // 9:00 - 16:59
    const isOpen = isWeekday && inHours;

    return { isOpen };
  }, []);

  // ---- auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate("/login"); // ajusta tu ruta
        return;
      }
      setUid(user.uid);
      setEmail(user.email || "");
      setLoading(false);
      clearChatNotifs(user.uid);

      // asegurar doc generalData
      const chatRef = doc(db, "chats", user.uid);
      const snap = await getDoc(chatRef);

      if (!snap.exists()) {
        await setDoc(
          chatRef,
          {
            createdAt: serverTimestamp(),
            lastMessage: "",
            lastMessageAt: serverTimestamp(),
            lastSenderType: "user",
            unreadForAdmin: false,
            unreadForUser: false,
            userEmail: user.email || "",
            userId: user.uid,
          },
          { merge: true }
        );
      } else {
        // al entrar, marcamos no-leído para usuario = false (opcional)
        await updateDoc(chatRef, { unreadForUser: false }).catch(() => {});
      }
    });

    return () => unsub();
  }, [navigate]);

  // ---- realtime mensajes
  useEffect(() => {
    if (!uid) return;

    const q = query(
      collection(db, "chats", uid, "messages"),
      orderBy("createdAt", "asc"),
      limit(200)
    );

    const unsub = onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMessages(rows);

      // auto-scroll
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    });

    return () => unsub();
  }, [uid]);

  async function sendMessage() {
    const clean = text.trim();
    if (!clean || !uid || sending) return;

    setSending(true);
    try {
      // 1) escribir mensaje
      await addDoc(collection(db, "chats", uid, "messages"), {
        createdAt: serverTimestamp(),
        senderId: uid,
        senderType: "user",
        text: clean,
      });

      // 2) actualizar generalData
      const chatRef = doc(db, "chats", uid);
      await setDoc(
        chatRef,
        {
          lastMessage: clean,
          lastMessageAt: serverTimestamp(),
          lastSenderType: "user",
          unreadForAdmin: true,
          unreadForUser: false,
          userEmail: email || "",
          userId: uid,
        },
        { merge: true }
      );

      setText("");
    } catch (e) {
      console.error(e);
      alert(t("chat.errorSend"));
    } finally {
      setSending(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  if (loading) {
    return (
      <div className="lp">
        <div className="lp-container" style={{ padding: 24 }}>
          <div className="chat-card">{t("chat.loading")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="lp">
      <div className="lp-container chat-page">
        {/* Header info */}
        <div className="chat-top">
          <div className="chat-titleRow">
            <h1 className="chat-title">{t("chat.title")}</h1>
            <div className={`chat-badge ${support.isOpen ? "open" : "closed"}`}>
              {support.isOpen ? t("chat.badge.open") : t("chat.badge.closed")}
            </div>
          </div>

          <p className="chat-guide">{t("chat.guide")}</p>

          <div className="chat-hours">
            <div className="chat-hours-left">
              <div className="chat-hours-line">
                <strong>{t("chat.hoursTitle")}</strong> {t("chat.hoursValue")}
              </div>
              <div className="chat-hours-line">
                <strong>{t("chat.etaTitle")}</strong> {t("chat.etaValue")}
              </div>
            </div>

            {!support.isOpen && (
              <div className="chat-outside">
                {t("chat.outsideHours")}
              </div>
            )}
          </div>
        </div>


        {/* REVISION DE PAGOS Y CREACIONES */}
       {!entLoading && !canSong && (
        <div className="ent-alert">
            <strong>{t("paywall.title")}</strong><br />
            {t("paywall.desc")}
            <div className="ent-alert-actions">
            <button className="btn btn-primary" onClick={() => navigate("/app/pagos")}>
            {t("paywall.button")}
            </button>
            <button className="btn btn-ghost" onClick={() => navigate("/app/chat-suport")}>
                {t("paywall.chat")}
            </button>
            </div>
        </div>
        )}

      {!entLoading && (
        <div className="ent-credits">
            {t("paywall.songsLeft", {
            left: (entitlements?.demoSongsLeft || 0) + (entitlements?.paidSongsCredits || 0)
            })}
        </div>
        )}




        {/* Chat box */}
        <div className="chat-card">
          <div className="chat-messages">
            {messages.length === 0 ? (
              <div className="chat-empty">
                {t("chat.empty")}
              </div>
            ) : (
              messages.map((m) => {
                const isMe = m.senderType === "user";
                const who = isMe ? t("chat.whoYou") : t("chat.whoSupport");

                return (
                  <div key={m.id} className={`chat-row ${isMe ? "me" : "them"}`}>
                    <div className="chat-meta">{who}</div>
                    <div className={`chat-bubble ${isMe ? "me" : "them"}`}>
                      {m.text}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="chat-inputRow">
            <textarea
              className="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t("chat.placeholder")}
              rows={2}
            />

            <button
              className="chat-sendBtn"
              onClick={sendMessage}
              disabled={sending || text.trim().length === 0}
            >
              {sending ? t("chat.sending") : t("chat.send")}
            </button>
          </div>

          <div className="chat-footnote">
            {t("chat.note")}
          </div>
        </div>
      </div>
    </div>
  );
}
